import { arrField, strField } from './materialize-fields.js';

/** A single inline/standalone assertion, visualized as its own row. */
export interface SpecAssertion {
  severity: string;
  condition: string;
}

export type SpecDocBlock =
  | { kind: 'frontmatter'; fields: Record<string, string> }
  | { kind: 'section'; title: string; markdown: string }
  | {
      kind: 'journey-step';
      nodeId: string;
      nodeType: string;
      /** Clean primary line — no node-type prefix (the type pill carries that). */
      title: string;
      /** Prose for the step (rendered as a styled note, not a raw blockquote). */
      description?: string;
      /**
       * Assertions to visualize — a standalone assertion node yields one entry;
       * an action node yields its inline assertions.
       */
      assertions: SpecAssertion[];
      /** Remaining authored detail lines (fields, versions, flags) as markdown. */
      details: string[];
    };

export interface RenderedStep {
  title: string;
  description?: string;
  assertions: SpecAssertion[];
  details: string[];
}

/**
 * Structured form of a node for the rich block view. Unlike the canonical
 * string renderer, this strips the node-type prefix (the UI shows a type pill),
 * lifts assertions into a list for visualization, and separates prose from
 * authored detail lines.
 */
export function renderStepStructured(node: {
  nodeType: string;
  configJson: Record<string, unknown> | null;
}): RenderedStep {
  const cfg = node.configJson ?? {};
  const empty = (over: Partial<RenderedStep> & { title: string }): RenderedStep => ({
    assertions: [],
    details: [],
    ...over,
  });
  switch (node.nodeType) {
    case 'navigate':
      return empty({ title: strField(cfg, 'pageUrl') ?? '?' });
    case 'action': {
      const title = strField(cfg, 'customActionTitle') ?? strField(cfg, 'title') ?? 'Action';
      const desc = strField(cfg, 'actionDescription') ?? strField(cfg, 'description');
      const details: string[] = [];
      for (const f of arrField(cfg, 'formFields')) {
        const name = strField(f, 'name');
        const value = strField(f, 'value');
        const type = strField(f, 'type');
        if (name && value !== undefined) {
          details.push(`field ${name}${type && type !== 'text' ? ` (${type})` : ''}: ${value}`);
        }
      }
      for (const c of arrField(cfg, 'declaredOutputs')) {
        if (typeof c === 'string') details.push(`capture: ${c}`);
      }
      const assertions: SpecAssertion[] = [];
      for (const a of arrField(cfg, 'inlineAssertions')) {
        const condition = strField(a, 'condition');
        if (condition) assertions.push({ severity: strField(a, 'severity') ?? 'fail', condition });
      }
      return { title, description: desc && desc !== title ? desc : undefined, assertions, details };
    }
    case 'assertion': {
      const condition = strField(cfg, 'condition') ?? strField(cfg, 'description') ?? '?';
      const severity = strField(cfg, 'severity') ?? 'fail';
      // The assertion is the content: the pill + assertion row carry it.
      return { title: '', assertions: [{ severity, condition }], details: [] };
    }
    case 'wait': {
      const dur =
        (cfg as { waitDuration?: number; delaySeconds?: number }).waitDuration ??
        (cfg as { delaySeconds?: number }).delaySeconds ??
        0;
      return empty({ title: `Wait ${dur}s` });
    }
    case 'login': {
      const role = strField(cfg, 'credentialTemplateName');
      const cred = strField(cfg, 'credentialName');
      const ref = role ?? (cred ? `credential ${cred}` : undefined);
      return empty({ title: ref ? `Switch role to ${ref}` : 'Log in' });
    }
    case 'seed':
      return empty({ title: strField(cfg, 'seedWorkflowName') ?? '?' });
    case 'setup':
      return empty({ title: strField(cfg, 'setupFlowName') ?? '?' });
    case 'condition':
      return empty({
        title: strField(cfg, 'conditionExpression') ?? strField(cfg, 'description') ?? '?',
      });
    case 'notification':
      return empty({
        title: strField(cfg, 'to') ?? '?',
        description: strField(cfg, 'body'),
      });
    case 'api_sequence':
      return empty({
        title: strField(cfg, 'customActionTitle') ?? strField(cfg, 'title') ?? 'API sequence',
      });
    case 'sandbox_action':
      return empty({ title: strField(cfg, 'actionSlug') ?? '?' });
    case 'mcp':
      return empty({
        title: strField(cfg, 'title') ?? 'MCP',
        description: strField(cfg, 'instructions'),
      });
    case 'end': {
      const outcome = strField(cfg, 'outcome') ?? 'pass';
      return empty({ title: outcome === 'pass' ? 'End' : `End (${outcome})` });
    }
    default:
      return empty({
        title: strField(cfg, 'title') ?? strField(cfg, 'customActionTitle') ?? node.nodeType,
      });
  }
}
